import { Actions } from "@client/actions";
import { type ContainerProps } from "@client/content/Containers/container";
import { type RootContainerProps } from "@client/content/types";
import { type EntityRef, type WithEntitiesSource, type WithId } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import { uniq } from "lodash";
import { type Reducer } from "redux";

type WrapReducerArgs<TState extends WithId> = {
  defaultState: TState;
  reducer: Reducer<TState, ReduxAction<any>>;
  previewState: TState;
  addon?: Reducer<TState, ReduxAction<any>>;
};
/**
 * ALL content types should use that function and dispatch actions with id property to properly address them
 */
export const wrapReducer =
  <TState extends WithId>(args: WrapReducerArgs<TState>) =>
  (id: string) =>
  (state: TState = { ...args.defaultState, id }, action: ReduxAction<any>): TState => {
    switch (action.type) {
      case Actions.SetModulePreview: {
        return { ...args.previewState, id };
      }
      case Actions.ResetModuleReducerState: {
        return { ...args.defaultState, id };
      }
    }

    if ((!action.data || action.data.id !== state.id) && (!action.params || action.params.id !== state.id)) {
      return args.addon ? args.addon(state, action) : state;
    }

    return args.addon ? args.addon(args.reducer(state, action), action) : args.reducer(state, action);
  };

export const getContainerProps = (
  props: RootContainerProps
): Pick<ContainerProps, "id" | "draggable" | "source" | "viewonly"> => ({
  id: props.id,
  draggable: props.draggable,
  source: props.source,
  viewonly: props.viewonly
});

export const getEntityRef = (props: WithId & WithEntitiesSource): EntityRef => ({
  id: props.id,
  source: props.source
});

export const getRootContainerProps = <P extends RootContainerProps>(props: P): RootContainerProps => ({
  id: props.id,
  source: props.source,
  TextComponent: props.TextComponent,
  StaticContainer: props.StaticContainer,
  viewonly: props.viewonly
});

export const getRootContainerPropsDep = <P extends RootContainerProps>(props: P): any[] =>
  Object.values(getRootContainerProps(props));

export const renderTrackingCodes = (data: { warehouseShippingCode: string | null }[]) => {
  const codes = uniq(data.filter((x) => x.warehouseShippingCode).map((x) => x.warehouseShippingCode));
  return codes.length ? codes.join(", ") : "-";
};
